/**
 * Sidebar navigation configuration.
 *
 * Single source of truth for the app's navigation (usability audit §4.1): eight
 * areas in the sidebar, each with the pages that belong together as tabs
 * (`tabs`). SectionTabs (AppLayout) shows the tab row of the area the current
 * URL belongs to; every page keeps its own URL. Each group
 * renders as a labeled semantic section (heading + <ul aria-labelledby>)
 * inside the single <nav>/<aside> landmark in Sidebar.tsx.
 *
 * `icon` values match the keys handled by Sidebar.tsx's resolveIcon()
 * switch statement — NOT raw lucide-react component names. `labelKey`
 * values use the flat `navGroup`/`navItem` object convention that matches
 * en.json (and all other locale files) — NOT a dotted 'nav.group.*' /
 * 'nav.item.*' convention. `requiresAdmin` gates the item behind the
 * existing admin-role check — filtered out via array .filter() before
 * render, never CSS-hidden.
 */

export interface NavItem {
  /** i18n key resolved via the project's own useTranslation hook */
  readonly labelKey: string;
  /**
   * Route path, copied verbatim from the pre-refactor Sidebar.tsx. Items
   * whose destination depends on the active aquarium (e.g. journal, tasks)
   * take a function instead of a literal string — Sidebar.tsx only calls it
   * once `requiresActiveAquarium` has confirmed an id is present.
   */
  readonly to: string | ((activeAquariumId: string) => string);
  /** Icon key handled by Sidebar.tsx's resolveIcon() switch */
  readonly icon: NavIconKey;
  /** NavLink `end` prop — true for routes that must not match on partial path */
  readonly end?: boolean;
  /** When true, item is only rendered for authenticated admin users */
  readonly requiresAdmin?: boolean;
  /**
   * When true, item is only rendered once an aquarium is active (matches
   * the pre-refactor Sidebar's `activeAquariumId != null` gate — most of
   * these items are also aquarium-scoped in their route, via a `to` function).
   */
  readonly requiresActiveAquarium?: boolean;
  /**
   * When true, item is only rendered while the active aquarium's supply system is
   * ZeoVit (#1784, utils/supplySystem.ts) — filtered out like the other gates.
   */
  readonly requiresZeovitSupplySystem?: boolean;
  /**
   * The pages of this area, shown as tabs. The sidebar entry links to the first
   * tab the user can open and is active on every tab's routes.
   */
  readonly tabs?: readonly NavTab[];
  /**
   * Voice journal "To review" inbox: shown while voice notes are available or
   * something waits for review, with the waiting count (Sidebar.tsx).
   */
  readonly captureReview?: boolean;
}

/**
 * One page of an area, shown as a tab. `path` is a route pattern of App.tsx;
 * `:aquariumId` is filled with the aquarium of the current URL or the active one.
 */
export interface NavTab {
  readonly labelKey: string;
  readonly path: string;
  /** Exact match only; otherwise sub-routes (detail pages, /new, …) belong to the tab too. */
  readonly end?: boolean;
  /** Further route patterns that belong to this tab (prefix match). */
  readonly alsoMatches?: readonly string[];
  /** Only while the active aquarium's supply system is ZeoVit (like NavItem). */
  readonly requiresZeovitSupplySystem?: boolean;
}

export interface NavGroup {
  /** i18n key for the group's section heading */
  readonly labelKey: string;
  readonly items: readonly NavItem[];
}

/**
 * Union of icon keys recognised by Sidebar.tsx's resolveIcon(). Keeping this
 * as a literal union (rather than a plain `string`) makes a typo in
 * navigationConfig's `icon` field a compile-time error instead of a silent
 * runtime fallback to `undefined`.
 */
export type NavIconKey =
  | 'dashboard'
  | 'review'
  | 'safety'
  | 'equipment'
  | 'aquarium'
  | 'overview'
  | 'icp'
  | 'measurements'
  | 'livestock'
  | 'coralCheck'
  | 'lighting'
  | 'flow'
  | 'mentor'
  | 'journal'
  | 'gallery'
  | 'timeline'
  | 'community'
  | 'comparisons'
  | 'tasks'
  | 'integrations'
  | 'dosing'
  | 'zeovit'
  | 'analytics'
  | 'alerts'
  | 'budget'
  | 'settings'
  | 'license'
  | 'admin'
  | 'auditLog'
  | 'logout';

// Eight areas, each a question a reef keeper has (usability audit §4.1): what is due
// today, how is the water, what do I dose, how are the animals, does the equipment
// run, what happened, ask the mentor, and the rest. Related pages are tabs inside
// them instead of 26 sidebar entries. `to` of an area is its first tab; Sidebar
// links to the first tab the user can open. `logout` is deliberately NOT here: it's
// a client-side action (AuthContext.logout()), not a route, and is rendered
// directly by Sidebar.tsx's footer button.
const AQ = '/aquariums/:aquariumId';

export const navigationConfig: readonly NavGroup[] = [
  {
    labelKey: 'navGroup.aquarium',
    items: [
      {
        labelKey: 'navItem.today', to: '/dashboard', icon: 'dashboard',
        tabs: [
          { labelKey: 'navTab.overview', path: '/dashboard', end: true },
          { labelKey: 'navItem.tasks', path: `${AQ}/tasks` },
          // Reef Guard findings; the header bell counts the open ones.
          { labelKey: 'navTab.notices', path: '/alerts' },
        ],
      },
      // Voice journal: everything ReefGuide picked up, waiting for the user's confirmation.
      { labelKey: 'navItem.review', to: '/review', icon: 'review', end: true, captureReview: true },
      {
        labelKey: 'navItem.water', to: id => `/aquariums/${id}/measurements`, icon: 'measurements', requiresActiveAquarium: true,
        tabs: [
          // /measurements/new and /measurements/session belong here too (prefix).
          { labelKey: 'navTab.values', path: `${AQ}/measurements` },
          { labelKey: 'navItem.icpAnalysis', path: `${AQ}/icp-analyses`, alsoMatches: [`${AQ}/icp`] },
          // Target values and, on the same page, the test intervals.
          { labelKey: 'navTab.targetsIntervals', path: `${AQ}/parameter-targets` },
        ],
      },
      {
        labelKey: 'navItem.dosing', to: id => `/aquariums/${id}/dosing`, icon: 'dosing', requiresActiveAquarium: true,
        tabs: [
          { labelKey: 'navTab.dosingPlans', path: `${AQ}/dosing` },
          // ZeoVit dosing module (history, new, detail, check); only for ZeoVit aquariums.
          { labelKey: 'navItem.zeovit', path: `${AQ}/zeovit-recommendations`, requiresZeovitSupplySystem: true },
        ],
      },
      {
        // The animals and their health: stock, coral check, quarantine when they arrive,
        // incidents when something goes wrong (Reef Guard #1824/#1826).
        labelKey: 'navItem.animalsHealth', to: id => `/aquariums/${id}/livestock`, icon: 'livestock', requiresActiveAquarium: true,
        tabs: [
          { labelKey: 'navItem.animals', path: `${AQ}/livestock` },
          { labelKey: 'navItem.coralCheck', path: `${AQ}/coral-check` },
          { labelKey: 'navItem.quarantine', path: `${AQ}/quarantine` },
          { labelKey: 'navItem.incidents', path: `${AQ}/incidents` },
        ],
      },
      {
        // What runs the tank: light, flow, equipment safety (#1828) and the connected devices.
        labelKey: 'navItem.equipment', to: id => `/aquariums/${id}/lighting`, icon: 'equipment', requiresActiveAquarium: true,
        tabs: [
          { labelKey: 'navItem.lighting', path: `${AQ}/lighting` },
          { labelKey: 'navItem.flow', path: `${AQ}/flow` },
          { labelKey: 'navItem.equipmentSafety', path: `${AQ}/equipment-safety` },
          // Active on every tab (/integrations/controller, /camera, /focustronic, /reefbot, /fauna-marin).
          { labelKey: 'navItem.integrations', path: `${AQ}/integrations` },
        ],
      },
      {
        labelKey: 'navItem.diary', to: id => `/aquariums/${id}/journal`, icon: 'journal', requiresActiveAquarium: true,
        tabs: [
          { labelKey: 'navTab.journalEntries', path: `${AQ}/journal` },
          { labelKey: 'navItem.gallery', path: `${AQ}/gallery`, alsoMatches: ['/images'] },
          // Photo comparisons stay one click away (audit #4).
          { labelKey: 'navItem.comparisons', path: `${AQ}/comparisons` },
          { labelKey: 'navItem.timeline', path: `${AQ}/timeline` },
        ],
      },
      {
        labelKey: 'navItem.mentor', to: id => `/aquariums/${id}/mentor`, icon: 'mentor', requiresActiveAquarium: true,
        tabs: [
          { labelKey: 'navTab.mentorChat', path: `${AQ}/mentor` },
          // Analytics: plain-language questions about the user's own data.
          { labelKey: 'navItem.analytics', path: '/analytics' },
        ],
      },
      {
        // The aquarium list (also one click away in the header's aquarium switcher), budget, community.
        labelKey: 'navItem.more', to: '/aquariums', icon: 'aquarium',
        tabs: [
          { labelKey: 'navItem.aquariums', path: '/aquariums', end: true, alsoMatches: ['/aquariums/new'] },
          { labelKey: 'navItem.budget', path: `${AQ}/budget` },
          // Public journal entries of all users (audit #11).
          { labelKey: 'navItem.community', path: '/community' },
        ],
      },
    ],
  },
  {
    labelKey: 'navGroup.account',
    items: [
      { labelKey: 'navItem.settings', to: '/settings', icon: 'settings', end: true },
      { labelKey: 'navItem.license', to: '/settings/license', icon: 'license', end: true },
      // Admin area (/admin/users, /admin/licenses, /admin/ai-usage, /admin/gdpr): active on every tab.
      { labelKey: 'navItem.admin', to: '/admin', icon: 'admin', requiresAdmin: true },
      { labelKey: 'navItem.auditLog', to: '/admin/audit-log', icon: 'auditLog', requiresAdmin: true },
    ],
  },
];
